import { Box, CircularProgress, TextField, Typography } from "@mui/material";
import React from "react";
import { Button } from "@aidergroup/ui-v2";
import { i18n } from "@/utils/i18n";
import SectionTitle from "@/components/ui/section-title";
import {
    useDeleteSignedReportAttachment,
    usePreviewSignedReportAttachment,
    useUploadSignedReport,
} from "./hooks";
import { Report } from "@/types/report";

interface Props {
    value: string;
    onChange(value: string): void;
    onSave(): void;
    saving: boolean;
    isDirty: boolean;
    attachments: Report["attachments"];
}

const ReviewNotes: React.FC<Props> = ({
    value,
    onChange,
    onSave,
    saving,
    isDirty,
    attachments,
}) => {
    const REVIEW_NOTES_MAX = 550;
    const REVIEW_NOTES_MAX_LINES = 5;
    const [signedReportName, setSignedReportName] = React.useState<string>("");
    const { uploading: signedUploading, upload } = useUploadSignedReport();
    const { deleting, remove } = useDeleteSignedReportAttachment();
    const { previewing, preview } = usePreviewSignedReportAttachment();

    const clampReviewNotes = (nextValue: string) => {
        const lines = nextValue.split(/\r?\n/);
        if (lines.length > REVIEW_NOTES_MAX_LINES) {
            return lines.slice(0, REVIEW_NOTES_MAX_LINES).join("\n");
        }
        return nextValue;
    };

    const noteValue = clampReviewNotes(value);
    const signedAttachments: Report["attachments"] = React.useMemo(() => {
        if (!Array.isArray(attachments)) return [];
        return attachments.filter(attachment => attachment.attachmentType === "SignedReport");
    }, [attachments]);

    return (
        <Box padding="30px 30px 30px 60px" display="flex" flexDirection="column" gap={2}>
            <SectionTitle title={i18n.t("reportReview.sectionTitle")} />
            <Typography variant="body2" color="text.secondary" maxWidth={680}>
                {i18n.t("reportReview.noteHelper")}
            </Typography>
            <Box
                display="grid"
                gridTemplateColumns={{ xs: "1fr", md: "minmax(0, 900px) 420px" }}
                gap={4}
                alignItems="stretch"
            >
                <Box p={2} borderRadius={2} border="1px solid" borderColor="divider">
                    <TextField
                        label={i18n.t("label.reviewNote")}
                        placeholder={i18n.t("placeholder.reviewNote")}
                        value={noteValue}
                        onChange={event => onChange(clampReviewNotes(event.target.value))}
                        fullWidth
                        multiline
                        minRows={5}
                        inputProps={{ maxLength: REVIEW_NOTES_MAX }}
                        InputProps={{ sx: { fontSize: 14 } }}
                        sx={{ maxWidth: 820 }}
                        helperText={i18n.t("reportReview.noteLimit", {
                            count: noteValue.length,
                            max: REVIEW_NOTES_MAX,
                            lines: noteValue.split(/\r?\n/).length,
                            maxLines: REVIEW_NOTES_MAX_LINES,
                        })}
                    />
                    <Box mt={2} display="flex" alignItems="center" gap={2}>
                        <Button variant="contained" onClick={onSave} disabled={!isDirty || saving}>
                            {i18n.t("button.saveReviewNote")}
                        </Button>
                        {saving && <CircularProgress size={20} />}
                    </Box>
                </Box>
                <Box
                    p={3}
                    borderRadius="16px"
                    bgcolor="#f8f9fa"
                    display="flex"
                    flexDirection="column"
                    gap={1}
                >
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {i18n.t("reportReview.signedUploadTitle")}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mb={1}>
                        {i18n.t("reportReview.signedUploadHelper")}
                    </Typography>

                    <input
                        id="signed-report-upload"
                        type="file"
                        accept="application/pdf,image/*"
                        hidden
                        disabled={signedUploading}
                        onChange={event => {
                            const file = event.target.files?.[0];
                            if (!file) return;
                            setSignedReportName(file.name);
                            upload(file);
                        }}
                    />
                    <Box display="flex" alignItems="center" gap={2} mb={1}>
                        <label htmlFor="signed-report-upload" style={{ width: "100%" }}>
                            <Button
                                variant="outlined"
                                component="span"
                                disabled={signedUploading}
                                fullWidth
                                sx={{
                                    borderStyle: "dashed",
                                    borderColor: "#ccc",
                                    color: "text.primary",
                                    py: 1.5,
                                    "&:hover": { borderColor: "#009ac8", borderStyle: "dashed" },
                                }}
                            >
                                {signedUploading ? (
                                    <CircularProgress size={20} />
                                ) : (
                                    i18n.t("reportReview.signedUploadButton")
                                )}
                            </Button>
                        </label>
                    </Box>

                    {signedReportName && (
                        <Typography variant="caption" color="text.secondary" display="block">
                            {signedReportName}
                        </Typography>
                    )}

                    {signedAttachments.length > 0 && (
                        <Box display="flex" flexDirection="column" gap={1} mb={1}>
                            {signedAttachments.map(attachment => (
                                <Box
                                    key={attachment.md5}
                                    display="flex"
                                    alignItems="center"
                                    gap={2}
                                    px={1.5}
                                    py={1}
                                    borderRadius="8px"
                                    bgcolor="#fff"
                                    border="1px solid #e0e0e0"
                                >
                                    <Typography
                                        variant="body2"
                                        noWrap
                                        title={attachment.name}
                                        sx={{ flex: 1, minWidth: 0 }}
                                    >
                                        {attachment.name}
                                    </Typography>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        disabled={Boolean(previewing[attachment.md5])}
                                        onClick={() => preview(attachment)}
                                    >
                                        {i18n.t("button.previewAttachment")}
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        color="error"
                                        size="small"
                                        disabled={Boolean(deleting[attachment.md5])}
                                        onClick={() => remove(attachment.md5)}
                                    >
                                        {i18n.t("button.removeAttachment")}
                                    </Button>
                                </Box>
                            ))}
                        </Box>
                    )}

                    <Typography variant="caption" color="text.secondary" sx={{ fontStyle: "italic" }}>
                        {i18n.t("reportReview.signedUploadDisclaimer")}
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
};

export default ReviewNotes;
